import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Shield, ArrowRight } from "lucide-react";

export default function StaffLogin() {
  const { loginWithCreds } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginWithCreds(form.email, form.password);
      const token = localStorage.getItem("jh_token");
      const { data: me } = await api.get("/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`مرحباً ${me.name || ""}`);
      if (me.is_driver) navigate("/driver", { replace: true });
      else if (me.is_admin) navigate("/admin", { replace: true });
      else if (me.is_restaurant_owner) navigate("/owner", { replace: true });
      else {
        toast.error("هذا الحساب ليس حساب موظف");
        navigate(location.state?.from || "/", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "بيانات غير صحيحة");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-start sm:items-center justify-center px-5 py-8 sm:py-14 bg-[#FAFAFA]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-[#1A1A1A] mb-4 shadow-lg shadow-black/20">
            <Shield className="w-9 h-9 text-white" strokeWidth={2.4} />
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-black text-[#1A1A1A]">
            دخول الموظفين
          </h1>
          <p className="text-[#6B6B6B] mt-2 text-sm">
            إدارة، سائقين، أصحاب المطاعم
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-[#EFEFEF] space-y-5">
          <div>
            <Label className="text-[#1A1A1A] text-sm font-medium">البريد الإلكتروني</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value.trim().toLowerCase() })}
              required
              autoFocus
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              dir="ltr"
              className="mt-2 rounded-2xl border-2 border-[#EAEAEA] focus:border-[#1A1A1A] py-6 text-base"
              data-testid="staff-email-input"
            />
          </div>

          <div>
            <Label className="text-[#1A1A1A] text-sm font-medium">كلمة المرور</Label>
            <div className="relative mt-2">
              <Input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                dir="ltr"
                className="rounded-2xl border-2 border-[#EAEAEA] focus:border-[#1A1A1A] py-6 text-base pe-12"
                data-testid="staff-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 end-3 flex items-center text-[#6B6B6B] hover:text-[#1A1A1A]"
                aria-label="إظهار كلمة المرور"
                data-testid="staff-toggle-password"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full rounded-2xl bg-[#1A1A1A] hover:bg-black text-white py-6 text-base font-bold disabled:opacity-50"
            data-testid="staff-login-submit"
          >
            {loading ? "..." : "تسجيل الدخول"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-sm text-[#E05D3A] hover:text-[#C24B2C] font-semibold"
            data-testid="customer-login-link"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            لست موظفاً؟ دخول العملاء
          </Link>
        </div>
      </div>
    </div>
  );
}
